import React, { useState } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { faGhost } from "@fortawesome/pro-duotone-svg-icons";
import {
  faQuestionCircle,
  faSensorOn,
  faStepForward,
  faThumbsDown,
  faWrench,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Center,
  Overlay,
  Paper,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";

import { useBasePath } from "@hooks";

import { ruleName } from "@pages/findings/utils";
import { useRuleStats } from "@pages/overview/hooks";
import {
  DEFAULT_RULES_EVENT,
  RuleAnalyticsItem,
  RulesEventType,
  WidgetInterface,
} from "@pages/overview/types";

import { SetUpCIUpsell } from "../SetUpCIUpsell";

const MinWidthNumberCell = styled.td`
  width: 1%;
  text-align: right;
`;

const ALL_RULES_TABS = [
  {
    icon: faSensorOn,
    title: "Most fired",
    eventType: "fired",
    teamsTierOnly: false,
  },
  {
    icon: faStepForward,
    title: "Most ignored",
    eventType: "ignored",
    teamsTierOnly: false,
  },
  {
    icon: faWrench,
    title: "Most fixed",
    eventType: "fixed",
    teamsTierOnly: false,
  },
  {
    icon: faThumbsDown,
    title: "Most disliked",
    eventType: "disliked",
    teamsTierOnly: true,
  },
];

const DEFAULT_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 10;

// goes on top of overlay
const UpsellOverlay = () => (
  <Box
    sx={{
      position: "absolute",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      zIndex: 2,
    }}
  >
    <Box sx={{ height: "200px", width: "200px" }}>
      <Paper withBorder shadow="sm" p="md">
        <SetUpCIUpsell toTrackItemName="rule stats" />
      </Paper>
    </Box>
  </Box>
);

const TableRow: React.FC<{ item?: RuleAnalyticsItem; isLoading?: boolean }> = ({
  item,
  isLoading,
}) => {
  const basePath = useBasePath();
  const history = useHistory();
  if (isLoading || !item) {
    return (
      <tr>
        <td>
          <Skeleton height={16} />
        </td>
        <MinWidthNumberCell>
          <Skeleton height={16} width={24} />
        </MinWidthNumberCell>
        <MinWidthNumberCell>
          <Skeleton height={16} width={24} />
        </MinWidthNumberCell>
        <MinWidthNumberCell>
          <Skeleton height={16} width={24} />
        </MinWidthNumberCell>
        <MinWidthNumberCell>
          <Skeleton height={16} width={24} />
        </MinWidthNumberCell>
      </tr>
    );
  }

  return (
    <tr
      key={item.rule_name}
      onClick={() =>
        history.push(`${basePath}/findings?rule=${item.rule_name}`)
      }
      style={{ cursor: "pointer" }}
    >
      <td>{ruleName(item.rule_name)}</td>
      <MinWidthNumberCell>{item.dislike_count}</MinWidthNumberCell>
      <MinWidthNumberCell>
        {`${Math.round(item.ignore_rate * 100)}%`}
      </MinWidthNumberCell>
      <MinWidthNumberCell>
        {`${Math.round(item.fix_rate * 100)}%`}
      </MinWidthNumberCell>
      <MinWidthNumberCell>{item.count}</MinWidthNumberCell>
    </tr>
  );
};

export const OverviewRulesWidget: React.FC<
  WidgetInterface & { hasTeamsTier: boolean }
> = ({ hasTeamsTier, hasProjects, reposFilter }) => {
  const theme = useMantineTheme();
  const [tab, setTab] = useState<RulesEventType>(DEFAULT_RULES_EVENT);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [tableIsHovered, setTableIsHovered] = useState<boolean>(false);
  const { data: ruleStats, isError } = useRuleStats(
    tab,
    reposFilter,
    moment().unix(),
    pageSize
  );

  const hasMoreButton =
    ruleStats &&
    pageSize < ruleStats.total_results_count &&
    pageSize < MAX_PAGE_SIZE;

  const tooltipContent =
    "Developers can interact with Semgrep PR comments to indicate that a finding was not useful";

  return (
    <Tabs
      data-testid="rules-tabs"
      id="rule-analytics"
      value={tab}
      onTabChange={(newTabId) => setTab(newTabId as RulesEventType)}
    >
      <Tabs.List pl="sm">
        {ALL_RULES_TABS.map((tab) => (
          <Tabs.Tab
            key={tab.eventType}
            value={tab.eventType}
            icon={<FontAwesomeIcon icon={tab.icon} />}
            disabled={(!hasTeamsTier && tab.teamsTierOnly) || !hasProjects}
          >
            {tab.title}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {ALL_RULES_TABS.map((tab) => (
        <Tabs.Panel
          value={tab.eventType}
          key={tab.eventType}
          sx={{ position: "relative" }}
          onMouseEnter={() => setTableIsHovered(true)}
          onMouseLeave={() => setTableIsHovered(false)}
        >
          {!hasProjects && tableIsHovered && (
            <>
              <Overlay opacity={0.6} color="#FFF" blur={1} zIndex={1} />
              <UpsellOverlay />
            </>
          )}
          <Table
            highlightOnHover={hasProjects}
            striped
            mt="xs"
            horizontalSpacing="lg"
          >
            <thead>
              <tr>
                <th>Rule</th>
                <th style={{ whiteSpace: "nowrap" }}>
                  Disliked{" "}
                  <Tooltip
                    label={tooltipContent}
                    multiline
                    width={280}
                    withArrow
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                  </Tooltip>
                </th>
                <th style={{ whiteSpace: "nowrap" }}>Ignored</th>
                <th style={{ whiteSpace: "nowrap" }}>Fix rate</th>
                <th style={{ whiteSpace: "nowrap" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {!isError &&
                hasProjects &&
                (ruleStats === undefined
                  ? [1, 2, 3, 4, 5].map((i) => (
                      <TableRow key={i} isLoading={true} />
                    ))
                  : ruleStats.rules.map((i) => (
                      <TableRow key={i.rule_name} item={i} />
                    )))}
            </tbody>
          </Table>
          {!isError && !hasProjects && (
            <Stack
              p="xl"
              sx={{ color: theme.colors["gray"][6] }}
              align="center"
              justify="center"
            >
              <FontAwesomeIcon
                icon={faGhost}
                size="3x"
                color={theme.colors["blue"][3]}
              />
              <Text align="center" size="sm">
                <Title order={4} weight="normal">
                  No rules found
                </Title>
                Enable CI to see rule stats
              </Text>
            </Stack>
          )}
          {!isError && hasProjects && ruleStats?.rules.length === 0 && (
            <Stack
              p="lg"
              sx={{ color: theme.colors["gray"][6] }}
              align="center"
              justify="center"
            >
              <FontAwesomeIcon
                icon={faGhost}
                size="3x"
                color={theme.colors["blue"][3]}
              />
              <Text align="center" size="sm">
                <Title order={4} weight="normal">
                  No rules found
                </Title>
                Try adjusting your filters
              </Text>
            </Stack>
          )}
          {isError && <Text color="red.8">Failed to load data</Text>}
          {hasMoreButton && (
            <Center py={"4px"}>
              <Button
                variant="subtle"
                onClick={() => setPageSize(pageSize + DEFAULT_PAGE_SIZE)}
              >
                More
              </Button>
            </Center>
          )}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
};
